import * as React from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    IconButton,
    Stack,
    Typography,
    Card,
} from '@mui/material';
import { TopToolbar, CreateButton, ExportButton } from 'react-admin';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import {
    addMonths,
    subMonths,
    addWeeks,
    subWeeks,
    addDays,
    subDays,
    format,
} from 'date-fns';
import { CalendarMode } from './MissionCalendar';
import { MissionCalendarContent } from './MissionCalendarContent';
import { MissionListFilter } from '../list/MissionListFilter'; // Reuse filter logic if desired, or build simple ones

interface MissionCalendarLayoutProps {
    anchorDate: Date;
    setAnchorDate: (date: Date) => void;
    mode: CalendarMode;
    setMode: (mode: CalendarMode) => void;
    rangeStart: Date;
    rangeEnd: Date;
}

export const MissionCalendarLayout = ({
    anchorDate,
    setAnchorDate,
    mode,
    setMode,
    rangeStart,
    rangeEnd,
}: MissionCalendarLayoutProps) => {
    const handlePrev = () => {
        if (mode === 'month') setAnchorDate(subMonths(anchorDate, 1));
        else if (mode === 'week') setAnchorDate(subWeeks(anchorDate, 1));
        else setAnchorDate(subDays(anchorDate, 1));
    };

    const handleNext = () => {
        if (mode === 'month') setAnchorDate(addMonths(anchorDate, 1));
        else if (mode === 'week') setAnchorDate(addWeeks(anchorDate, 1));
        else setAnchorDate(addDays(anchorDate, 1));
    };

    const handleToday = () => {
        setAnchorDate(new Date());
    };

    const getLabel = () => {
        if (mode === 'month') return format(anchorDate, 'MMMM yyyy');
        if (mode === 'week') {
            return `${format(rangeStart, 'MMM d')} - ${format(rangeEnd, 'MMM d, yyyy')}`;
        }
        return format(anchorDate, 'EEEE, MMMM d, yyyy');
    };

    return (
        <Stack direction="row" alignItems="flex-start" mt={2} width="100%">
            {/* Reuse MissionListFilter for sidebar filtering if wanted, but Calendar filter usually needs specific setup. We will reuse MissionListFilter for consistency */}
            <MissionListFilter disableSaveQuery />

            <Box flex={1}>
                <Card sx={{ p: 2, mb: 2 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleToday}
                                startIcon={<TodayIcon />}
                            >
                                Today
                            </Button>
                            <IconButton onClick={handlePrev} size="small">
                                <ChevronLeftIcon />
                            </IconButton>
                            <IconButton onClick={handleNext} size="small">
                                <ChevronRightIcon />
                            </IconButton>
                            <Typography
                                variant="h6"
                                sx={{ ml: 2, minWidth: 200 }}
                            >
                                {getLabel()}
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <ButtonGroup size="small" variant="outlined">
                                <Button
                                    variant={
                                        mode === 'month'
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    onClick={() => setMode('month')}
                                >
                                    Month
                                </Button>
                                <Button
                                    variant={
                                        mode === 'week'
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    onClick={() => setMode('week')}
                                >
                                    Week
                                </Button>
                                <Button
                                    variant={
                                        mode === 'day'
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    onClick={() => setMode('day')}
                                >
                                    Day
                                </Button>
                            </ButtonGroup>
                            <TopToolbar sx={{ minHeight: 'auto', pt: 0 }}>
                                <CreateButton />
                                <ExportButton />
                            </TopToolbar>
                        </Stack>
                    </Stack>
                </Card>

                <MissionCalendarContent
                    mode={mode}
                    setMode={setMode}
                    setAnchorDate={setAnchorDate}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                />
            </Box>
        </Stack>
    );
};
